import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Database, Link2 } from 'lucide-react';
import { toast } from 'sonner';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { Sheet, SheetContent, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { cn } from '@/lib/utils';
import { apiFetch } from '@/lib/api-client';
import {
  DmpConsolePanel, addressLine,
  type DmpConsoleRequest, type DmpLookupPrefill, type DmpLookupTicketCtx,
} from './DmpConsolePanel';
import type { DmpLookupResponse } from '@uniticket/shared';

// « Console DMP » (user ask 2026-07-30): a GLOBAL read-only drawer — type a jvid (or click any jvid
// anywhere in the app via <JvidLink>) and see the address's full DMP picture: reference + live
// attributes + the whole building. Mounted once in AppLayout so it opens over any page without
// navigation. Strictly read-only — no lock, no guard, visible to every internal user.
//
// The console's own content lives in DmpConsolePanel (extracted 2026-08-07 so the standalone page a
// BUILD account gets shows exactly the same tool). What stays here is what only the drawer does: the
// sheet, the global open() entry point, and the ONE write — « lier ce jvid au ticket ».

export type { DmpLookupPrefill, DmpLookupTicketCtx } from './DmpConsolePanel';

const Ctx = createContext<{ open: (jvid?: number | null, prefill?: DmpLookupPrefill, ticket?: DmpLookupTicketCtx) => void }>({ open: () => {} });

/** Open the DMP console from anywhere: `useDmpLookup().open(jvid)`. */
export const useDmpLookup = () => useContext(Ctx);

/** A clickable jvid — opens the DMP console preloaded. stopPropagation so clickable rows keep working. */
export function JvidLink({ jvid, className }: { jvid: number; className?: string }) {
  const { open } = useDmpLookup();
  const { t } = useTranslation(['tickets']);
  return (
    <button
      type="button"
      title={t('tickets:dmpLookup.linkTitle')}
      onClick={(e) => { e.stopPropagation(); open(jvid); }}
      className={cn('underline decoration-dotted decoration-slate-300 underline-offset-2 hover:text-brand-rose', className)}
    >
      {jvid}
    </button>
  );
}

export function DmpLookupProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation(['tickets']);
  const [isOpen, setIsOpen] = useState(false);
  const [request, setRequest] = useState<DmpConsoleRequest | null>(null);

  // Ticket context — set at EVERY open() so a previous ticket's context can never leak into a
  // console opened from the queue or a plain JvidLink.
  const [ticketCtx, setTicketCtx] = useState<DmpLookupTicketCtx | null>(null);
  const [linkData, setLinkData] = useState<DmpLookupResponse | null>(null);
  const [linking, setLinking] = useState(false);

  const open = useCallback((jvid?: number | null, prefill?: DmpLookupPrefill, ticket?: DmpLookupTicketCtx) => {
    setTicketCtx(ticket ?? null);
    setLinkData(null);
    setIsOpen(true);
    // The nonce is what makes a repeated open() of the SAME jvid reload; a request carrying neither a
    // jvid nor a street leaves the panel as it was, so reopening keeps the previous result on screen.
    setRequest((r) => ({ jvid: jvid ?? null, prefill, nonce: (r?.nonce ?? 0) + 1 }));
  }, []);

  // "Link this jvid to the open ticket" (user ask 2026-07-31): same endpoint + full-reload as the
  // no-address card's link form — the ticket then reopens in its normal validation mode.
  const linkRecap = linkData
    ? [
        addressLine(linkData.reference.streetName, linkData.reference.houseNumber, linkData.reference.houseNumberExtension)
          + (linkData.reference.busNumber?.trim() ? ` / ${linkData.reference.busNumber}` : ''),
        linkData.reference.postalCode,
        linkData.reference.city ?? linkData.reference.municipality,
      ].filter(Boolean).join(' · ')
    : '';

  async function linkToTicket() {
    if (!ticketCtx || !linkData) return;
    setLinking(true);
    try {
      await apiFetch(`/tickets/${ticketCtx.ticketId}/link-address`, {
        method: 'POST',
        body: JSON.stringify({ jvid: linkData.reference.jvid }),
      });
      toast.success(t('tickets:dmpLookup.linkDone', { jvid: linkData.reference.jvid, ticket: ticketCtx.ticketNumber }));
      setLinkData(null);
      setIsOpen(false);
      window.location.reload();
    } catch (err) {
      setLinkData(null);
      toast.error((err as Error)?.message || t('tickets:dmpLookup.linkError'));
    } finally {
      setLinking(false);
    }
  }

  return (
    <Ctx.Provider value={{ open }}>
      {children}
      <Sheet open={isOpen} onOpenChange={setIsOpen}>
        <SheetContent side="right" className="w-full sm:max-w-3xl overflow-y-auto p-5">
          <SheetTitle className="flex items-center gap-2">
            <Database className="w-5 h-5 text-brand-rose" /> {t('tickets:dmpLookup.title')}
          </SheetTitle>
          <SheetDescription className="mt-1">{t('tickets:dmpLookup.subtitle')}</SheetDescription>

          <div className="mt-4">
            <DmpConsolePanel
              request={request}
              ticket={ticketCtx}
              onLink={setLinkData}
              linkBusy={linking}
            />
          </div>
        </SheetContent>
      </Sheet>
      {/* Outside the Sheet: its slide-in transform would anchor a fixed overlay to the sheet, not the viewport. */}
      <ConfirmDialog
        open={linkData !== null}
        tone="brand"
        icon={<Link2 className="w-5 h-5" />}
        title={t('tickets:dmpLookup.linkConfirmTitle')}
        message={t('tickets:dmpLookup.linkConfirmBody', {
          jvid: linkData?.reference.jvid ?? '',
          address: linkRecap,
          ticket: ticketCtx?.ticketNumber ?? '',
        })}
        confirmLabel={t('tickets:dmpLookup.linkBtn', { ticket: ticketCtx?.ticketNumber ?? '' })}
        cancelLabel={t('tickets:validation.reclassify.cancel')}
        busy={linking}
        onCancel={() => { if (!linking) setLinkData(null); }}
        onConfirm={() => void linkToTicket()}
      />
    </Ctx.Provider>
  );
}
